// src/components/chat/ChatWindow.jsx
import React, { useEffect, useState, useContext, useRef } from "react";
import toast from "react-hot-toast";
import api from "../../utils/api";
import ChatMessage from "./ChatMessage";
import { AuthContext } from "../../context/AuthContext";

export default function ChatWindow({ otherUserId, listingId }) {
  const { user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [initialLoading, setInitialLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Load conversation once when otherUserId/listingId changes
  useEffect(() => {
    if (!otherUserId) return;

    async function load() {
      setInitialLoading(true);
      try {
        const res = await api.get("/chat", {
          params: {
            withUserId: otherUserId,
            listingId: listingId || undefined,
          },
        });
        setMessages(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setInitialLoading(false);
      }
    }

    load();
  }, [otherUserId, listingId]);

  async function send() {
    if (!text.trim() || !otherUserId) return;

    const content = text.trim();
    setText("");

    try {
      const res = await api.post("/chat", {
        receiverId: otherUserId,
        listingId,
        content,
      });

      // Append new message locally – no reload needed
      setMessages((prev) => [...prev, res.data]);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to send message");
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  if (!otherUserId) {
    return (
      <div className="h-[70vh] flex items-center justify-center text-gray-400">
        Select a conversation or click a chat button from a listing.
      </div>
    );
  }

  return (
    // 🔴 Key change: give the whole chat window a fixed height
    <div className="h-[70vh] flex flex-col">
      <div className="flex items-center gap-2 border-b pb-3 mb-3">
        <div className="font-semibold text-lg">Chat</div>

        <div className="text-sm text-gray-500">
          Listing #{listingId || "-"}
        </div>
      </div>

      {/* 🔴 Key change: min-h-0 + overflow-y-auto so only this part scrolls */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 bg-gray-50 rounded-lg">
        {initialLoading && (
          <div className="text-xs text-gray-400 mb-2">Loading messages…</div>
        )}

        {messages.map((m) => (
          <ChatMessage
            key={m.id}
            fromMe={m.senderId === user?.id}
            text={m.content}
            createdAt={m.createdAt}
          />
        ))}

        {/* dummy div to scroll to bottom */}
        <div ref={messagesEndRef} />
      </div>

      <div className="mt-4 flex gap-3 items-center">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your message..."
          className="flex-1 border rounded-xl p-3 resize-none h-10"
        />
        <button
          onClick={send}
          className="bg-primary text-white px-4 py-2 rounded-xl"
        >
          Send
        </button>
      </div>
    </div>
  );
}
